<template>
  <div
    v-if="$store.state.uploadOpen"
    style="
      position: fixed;
      inset: 0;
      z-index: 50;
      background: rgba(8, 9, 11, 0.72);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 32px;
    "
    @click.self="close()">
    <div
      style="
        width: 720px;
        max-width: 100%;
        max-height: 84vh;
        display: flex;
        flex-direction: column;
        background: var(--back-content);
        border: 1px solid var(--border);
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6);
      ">
      <div header style="height: 52px; min-height: 52px; max-height: 52px; gap: 11px">
        <span style="font-size: 15px; font-weight: 700; color: #fff">Upload</span>
        <span nowrap class="mono" style="font-size: 10px; letter-spacing: 0.14em; color: var(--hsl-46); overflow: hidden; text-overflow: ellipsis">
          {{ folderTitle.toUpperCase() }}
        </span>
        <v-btn icon small class="delbtn" style="margin-left: auto" @click="close()"><v-icon size="18">mdi-close</v-icon></v-btn>
      </div>

      <div style="flex: 1; overflow: auto; min-height: 0; padding: 16px 20px 20px">
        <div horizontal align-center style="gap: 8px; margin-bottom: 11px">
          <span class="micro">Metadata</span>
          <span style="flex: 1; height: 1px; background: var(--border)"></span>
        </div>
        <MetadataDisplay v-if="metadata.length" :items="metadata" :required="requiredIds"></MetadataDisplay>
        <div v-else class="mono" style="padding: 18px; text-align: center; color: var(--hsl-40)">
          No upload metadata set configured — Admin → Uploads.
        </div>

        <div horizontal align-center style="gap: 8px; margin: 22px 0 11px">
          <span class="micro">Files</span>
          <span style="flex: 1; height: 1px; background: var(--border)"></span>
          <input ref="picker" type="file" multiple style="display: none" @change="pick($event)" />
          <v-btn small class="addbtn" @click="$refs.picker.click()">+ Choose files</v-btn>
        </div>
        <div style="display: flex; flex-direction: column; gap: 9px">
          <div
            v-for="(file, index) in files"
            :key="index"
            horizontal
            align-center
            nowrap
            style="gap: 9px; background: var(--back-header); border: 1px solid var(--border); border-radius: 9px; padding: 9px 12px">
            <span nowrap style="flex: 1; font-size: 12.5px; overflow: hidden; text-overflow: ellipsis">{{ file.name }}</span>
            <span class="mono" style="font-size: 10px; color: var(--hsl-46)">{{ humanSize(file.size) }}</span>
          </div>
          <div v-if="!files.length" class="mono" style="padding: 18px; text-align: center; color: var(--hsl-40)">No files chosen yet.</div>
        </div>

        <template v-if="$store.state.uploads.length">
          <div horizontal align-center style="gap: 8px; margin: 22px 0 11px">
            <span class="micro">In flight</span>
            <span style="flex: 1; height: 1px; background: var(--border)"></span>
            <v-btn small @click="$store.state.clearFinishedUploads()">Clear finished</v-btn>
          </div>
          <div style="display: flex; flex-direction: column; gap: 9px">
            <div
              v-for="entry in $store.state.uploads"
              :key="entry.id"
              style="background: var(--back-header); border: 1px solid var(--border); border-radius: 9px; padding: 9px 12px">
              <div horizontal align-center nowrap style="gap: 9px">
                <span nowrap style="flex: 1; font-size: 12.5px; overflow: hidden; text-overflow: ellipsis">{{ entry.name }}</span>
                <span class="mono" style="font-size: 10px; color: var(--hsl-46)">{{ humanSize(entry.size) }}</span>
                <span class="pill" :style="pillStyle(UPLOAD_STATUS[entry.status].color)">{{ UPLOAD_STATUS[entry.status].label }}</span>
                <v-btn v-if="entry.status === 'uploading'" icon small title="Pause" @click="$store.state.pauseUpload(entry)">
                  <v-icon size="16">mdi-pause</v-icon>
                </v-btn>
                <v-btn v-if="entry.status === 'paused' || entry.status === 'error'" icon small title="Resume" @click="$store.state.resumeUpload(entry)">
                  <v-icon size="16">mdi-play</v-icon>
                </v-btn>
                <v-btn v-if="entry.status !== 'done'" icon small class="delbtn" title="Cancel" @click="$store.state.cancelUpload(entry)">
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
              </div>
              <v-progress-linear :value="entry.progress" height="4" style="margin-top: 8px"></v-progress-linear>
              <div v-if="entry.error" class="mono" style="margin-top: 6px; font-size: 10px; color: var(--error)">{{ entry.error }}</div>
            </div>
          </div>
        </template>
      </div>

      <div footer style="height: 46px; min-height: 46px; max-height: 46px; gap: 10px">
        <span class="mono" style="font-size: 10px; color: var(--hsl-46)">{{ hint }}</span>
        <div style="margin-left: auto; display: flex; gap: 8px">
          <v-btn small class="addbtn" :disabled="!!hint" @click="start()">Start upload</v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { pillStyle } from "@/scripts/assetStatus.js";
import { isFilled } from "@/scripts/metadataFields.js";

const UNITS = ["B", "KB", "MB", "GB", "TB"];
const UPLOAD_STATUS = {
  uploading: { label: "Uploading", color: "#E6A23A" },
  paused: { label: "Paused", color: "#7C8190" },
  done: { label: "Done", color: "#3DA46B" },
  error: { label: "Failed", color: "#C23A2B" },
};

export default {
  data() {
    return { files: [], metadata: [], UPLOAD_STATUS };
  },
  computed: {
    folderTitle() {
      const parent = this.$store.state.getLastFilterParentAsset();
      return (parent && parent.title) || "root";
    },
    requiredIds() {
      const config = this.$store.state.uploadConfig;
      return (config && config.requiredFieldIds) || [];
    },
    missingField() {
      return this.metadata.find((field) => this.requiredIds.indexOf(field.id) !== -1 && !isFilled(field));
    },
    /** Empty means "ready to go"; anything else is both the reason and the disabled flag. */
    hint() {
      if (!this.files.length) return "Choose at least one file";
      if (this.missingField) return "Required: " + (this.missingField.label || "field");
      return "";
    },
  },
  watch: {
    "$store.state.uploadOpen"(open) {
      if (open) this.reset();
    },
  },
  mounted() {
    window.addEventListener("keydown", this.onKey);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKey);
  },
  methods: {
    onKey(event) {
      if (event.key === "Escape") this.close();
    },
    close() {
      this.$store.state.uploadOpen = false;
    },
    // A fresh copy of the configured set, so editing the form never touches the set itself.
    reset() {
      const config = this.$store.state.uploadConfig;
      const set = this.$store.state.metadataSets.find((s) => s.id === (config && config.metadataSetId));
      this.metadata = JSON.parse(JSON.stringify((set && set.metadata) || []));
      this.files = [];
    },
    pick(event) {
      this.files = Array.from(event.target.files);
      event.target.value = "";
    },
    // The uploads live in the store, so closing the dialog does not abort them.
    start() {
      this.$store.state.queueUploads(this.files, this.metadata);
      this.files = [];
    },
    humanSize(bytes) {
      let size = bytes || 0;
      let unit = 0;
      while (size >= 1024 && unit < UNITS.length - 1) {
        size /= 1024;
        unit++;
      }
      return (unit === 0 ? size : size.toFixed(1)) + " " + UNITS[unit];
    },
    pillStyle,
  },
};
</script>
